import React, { createContext, ReactNode, useMemo, useCallback } from 'react';
import type { TFunction } from 'i18next';
import { NewOrderInterface } from '@adapters/bigcommerce/types/order';
import { ConfirmedReturnVariant, ReturnVariant } from '@utils/returns/types';
import type { NewOrderItem } from '@adapters/bigcommerce/types/order';
import { ProductDetailForReturns } from '@adapters/bigcommerce/operations/product/getProductDetailsForReturns';
import { MEC_SELLER_NAME, MEC_SELLER_SYSTEM } from '@utils/constants';

export interface State {
  order?: NewOrderInterface | null;
  returnVariants: ReturnVariant[];
  selectedReturnVariants: ConfirmedReturnVariant[];
  returnRequestNumber: string | null;
}

export interface Actions {
  setReturnOrder: (order: NewOrderInterface) => void;
  setVariantsToReturn: (
    orderVariants: NewOrderItem[],
    productDetails: ProductDetailForReturns[],
    options: {
      shippedItemSkus: string[];
      orderDate: Date;
      t: TFunction<'returns'>;
      sellerName?: string;
    }
  ) => void;
  setSelectedVariants: (variants: ConfirmedReturnVariant[]) => void;
  setReturnRequestNumber: (returnRequestNumber: string) => void;
  clearReturnsData: () => void;
}

type Action =
  | {
      type: 'SET_RETURN_ORDER';
      order: NewOrderInterface;
    }
  | {
      type: 'SET_RETURN_VARIANTS';
      variants: ReturnVariant[];
    }
  | {
      type: 'SET_SELECTED_VARIANTS';
      variants: ConfirmedReturnVariant[];
    }
  | {
      type: 'SET_RETURN_REQUEST_NUMBER';
      returnRequestNumber: string | null;
    }
  | {
      type: 'CLEAR_RETURNS_DATA';
    };

interface Inferred {
  marketplaceSellerName?: string | null;
  marketplaceSellerSystem?: string | null;
}

export type ReturnsState = State & Actions & Inferred;

export const createInitialReturnsState = () => ({
  order: null,
  returnVariants: [],
  selectedReturnVariants: [],
  marketplaceSellerName: null,
  marketplaceSellerSystem: null,
  returnRequestNumber: null,
  setReturnOrder: () => null,
  setVariantsToReturn: () => null,
  setSelectedVariants: () => null,
  setReturnRequestNumber: () => null,
  clearReturnsData: () => null,
});

export const ReturnsContext = createContext<ReturnsState>(
  createInitialReturnsState()
);

ReturnsContext.displayName = 'ReturnsUIContext';

const returnsReducer = (state: State, action: Action) => {
  switch (action.type) {
    case 'SET_RETURN_ORDER': {
      return {
        ...state,
        order: action.order,
      };
    }
    case 'SET_RETURN_VARIANTS': {
      return {
        ...state,
        returnVariants: action.variants,
      };
    }
    case 'SET_SELECTED_VARIANTS': {
      return {
        ...state,
        selectedReturnVariants: action.variants,
      };
    }
    case 'SET_RETURN_REQUEST_NUMBER': {
      return {
        ...state,
        returnRequestNumber: action.returnRequestNumber,
      };
    }
    case 'CLEAR_RETURNS_DATA': {
      return {
        ...state,
        order: null,
        returnVariants: [],
        selectedReturnVariants: [],
        returnRequestNumber: null,
      };
    }
    default: {
      return { ...state };
    }
  }
};

export type ReturnsContextProps = {
  children: ReactNode;
};

export const ReturnsContextProvider = ({ children }: ReturnsContextProps) => {
  const initialReturnsState: State = createInitialReturnsState();

  const [state, dispatch] = React.useReducer(
    returnsReducer,
    initialReturnsState
  );

  const setReturnOrder = useCallback((order: NewOrderInterface) => {
    dispatch({ type: 'SET_RETURN_ORDER', order: order });
  }, []);

  const setVariantsToReturn = useCallback(
    (
      orderVariants: NewOrderItem[],
      productDetails: ProductDetailForReturns[],
      options: {
        shippedItemSkus: string[];
        orderDate: Date;
        t: TFunction<'returns'>;
        sellerName?: string;
      }
    ) => {
      if (
        !options ||
        !options.shippedItemSkus ||
        !options.orderDate ||
        !options.t
      ) {
        dispatch({ type: 'SET_RETURN_VARIANTS', variants: [] });
        return;
      }

      // Merge product details into order variants
      const itemsMap = new Map<string, ProductDetailForReturns>();
      productDetails.forEach((variant) => {
        itemsMap.set(variant.variantSku, variant);
      });

      const { shippedItemSkus, orderDate, t, sellerName } = options;

      import('@utils/returns/getItemEligibility').then(
        ({ getItemEligibility }) => {
          const enrichedVariants = orderVariants
            .filter((item) => {
              if (sellerName) {
                return item.sellerName === sellerName;
              }
              return true;
            })
            .map((item) => {
              const data = itemsMap.get(item.sku);
              const hasShipped = shippedItemSkus.includes(item.sku);
              const eligibilityResult = getItemEligibility(
                t,
                {
                  erp: data?.parsedERP ?? null,
                },
                orderDate,
                hasShipped
              );
              return {
                ...item,
                categories: data?.categories ?? [],
                variantId: data?.variantId ?? item.variant_id?.toString(),
                productId: data?.productId ?? item.product_id,
                variantSku: data?.variantSku ?? item.sku,
                productSku: item.style,
                brand: data?.brand ?? '',
                weight: data?.weight ?? 0,
                shippingRestrictions: data?.parsedShippingRestrictions ?? null,
                marketplaceSellerName: item.sellerName ?? MEC_SELLER_NAME,
                marketplaceSellerSystem: item.sellerSystem ?? MEC_SELLER_SYSTEM,
                refundStatus: item.refundStatus,
                returnEligibility: {
                  isEligible: eligibilityResult.eligible,
                  shortMessage: eligibilityResult.shortMessage,
                  longMessage: eligibilityResult.longMessage,
                },
              };
            });
          dispatch({ type: 'SET_RETURN_VARIANTS', variants: enrichedVariants });
        }
      );
    },
    [dispatch]
  );

  const setSelectedVariants = useCallback(
    (variants: ConfirmedReturnVariant[]) => {
      dispatch({ type: 'SET_SELECTED_VARIANTS', variants });
    },
    []
  );

  const setReturnRequestNumber = useCallback((returnRequestId: string) => {
    dispatch({
      type: 'SET_RETURN_REQUEST_NUMBER',
      returnRequestNumber: returnRequestId,
    });
  }, []);

  const clearReturnsData = useCallback(() => {
    dispatch({ type: 'CLEAR_RETURNS_DATA' });
  }, []);

  const value = useMemo(() => {
    return {
      ...state,
      setReturnOrder,
      setVariantsToReturn,
      setSelectedVariants,
      setReturnRequestNumber,
      clearReturnsData,
      marketplaceSellerName: state.selectedReturnVariants.length
        ? state.selectedReturnVariants[0].marketplaceSellerName
        : null,
      marketplaceSellerSystem: state.selectedReturnVariants.length
        ? state.selectedReturnVariants[0].marketplaceSellerSystem
        : null,
    };
  }, [
    setReturnOrder,
    setVariantsToReturn,
    setSelectedVariants,
    setReturnRequestNumber,
    clearReturnsData,
    state,
  ]);

  return (
    <ReturnsContext.Provider value={value}>{children}</ReturnsContext.Provider>
  );
};

export const useReturns = () => {
  const context = React.useContext(ReturnsContext);
  if (context === undefined) {
    throw new Error(`useReturns must be used within a ReturnsContext provider`);
  }
  return context;
};
